import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2, Plus, RefreshCw, Pause, Play, Eye, Check, X, Loader2, AlertTriangle,
  ChevronRight, Database, Shield, Trash2, Download, FileText, Settings2, Pencil,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { api } from "../lib/api";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  revoked: "bg-rose-100 text-rose-800",
  paused: "bg-slate-200 text-slate-700",
  draft: "bg-slate-100 text-slate-700",
  active: "bg-emerald-100 text-emerald-800",
  disabled: "bg-slate-200 text-slate-500",
  pending_review: "bg-amber-100 text-amber-800",
  auto_published: "bg-sky-100 text-sky-800",
  rejected: "bg-rose-100 text-rose-800",
  unchanged: "bg-slate-100 text-slate-500",
  disappeared: "bg-rose-50 text-rose-700",
  error: "bg-rose-100 text-rose-800",
  running: "bg-sky-100 text-sky-800",
  ok: "bg-emerald-100 text-emerald-800",
  partial: "bg-amber-100 text-amber-800",
};

const Pill = ({ value }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_COLORS[value] || "bg-slate-100 text-slate-600"}`}>
    {value || "—"}
  </span>
);

export default function AdminMakelaarFeeds() {
  const [summary, setSummary] = useState(null);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [showNewOffice, setShowNewOffice] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, o] = await Promise.all([
        api("/admin/partner-import/summary"),
        api("/admin/partner-offices"),
      ]);
      setSummary(s);
      setOffices(o.offices || []);
    } catch (e) {
      toast.error(e.message || "Kon dashboard niet laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  if (selectedOffice) {
    return <OfficeDetail officeId={selectedOffice} onBack={() => { setSelectedOffice(null); refresh(); }} />;
  }

  return (
    <div className="mx-auto max-w-7xl p-6" data-testid="admin-partner-feeds">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <nav className="mb-2 text-xs text-slate-500"><Link to="/admin" className="hover:text-brandblue">Admin</Link> → Makelaar-feeds</nav>
          <h1 className="font-display text-3xl font-extrabold text-navy">Makelaar-feeds</h1>
          <p className="mt-1 text-sm text-slate-600">Auto-import via XML/CSV-feeds of een website-scraper. Nieuwe woningen gaan eerst door een reviewqueue voordat ze gepubliceerd worden.</p>
        </div>
        <Button onClick={() => setShowNewOffice(true)} className="rounded-xl bg-brandblue hover:bg-brandblue/90" data-testid="btn-new-office">
          <Plus size={16} className="mr-2" /> Nieuwe makelaar
        </Button>
      </div>

      {summary && (
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard label="Makelaars" value={`${summary.offices_approved} / ${summary.offices_total}`} hint="goedgekeurd / totaal" />
          <SummaryCard label="Actieve feeds" value={summary.feeds_active} />
          <SummaryCard label="In review" value={summary.pending_items} warn={summary.pending_items > 0} />
          <SummaryCard label="Fouten / verdwenen" value={`${summary.error_items} / ${summary.disappeared_items}`} warn={summary.error_items > 0 || summary.disappeared_items > 0} />
        </div>
      )}

      <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-sm">
        <div className="border-b border-border bg-slate-50 px-5 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
          Aangesloten makelaars
        </div>
        {loading ? (
          <div className="flex items-center justify-center p-10 text-slate-500"><Loader2 className="mr-2 animate-spin" size={16} /> Laden...</div>
        ) : offices.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500">Nog geen aangesloten makelaars. Begin met een nieuwe toe te voegen.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr className="border-b border-border bg-slate-50">
                <Th>Naam</Th><Th>Samenwerking</Th><Th>Toestemming</Th><Th>Feeds</Th><Th>In review</Th><Th>Gepubliceerd</Th><Th />
              </tr>
            </thead>
            <tbody>
              {offices.map((o) => (
                <tr key={o.id} className="border-b border-border/70 hover:bg-slate-50" data-testid={`office-row-${o.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-semibold text-navy">{o.name}</div>
                    <div className="text-xs text-slate-500">{o.kvk ? `KvK ${o.kvk}` : ""} {o.website ? ` · ${o.website}` : ""}</div>
                  </td>
                  <td className="px-5 py-3"><Pill value={o.cooperation_status} /></td>
                  <td className="px-5 py-3"><Pill value={o.consent_status} /></td>
                  <td className="px-5 py-3 text-slate-600">{o.feed_count}</td>
                  <td className="px-5 py-3 text-slate-600">{o.pending_count}</td>
                  <td className="px-5 py-3 text-slate-600">{o.published_count}</td>
                  <td className="px-5 py-3 text-right">
                    <button onClick={() => setSelectedOffice(o.id)} className="inline-flex items-center text-brandblue hover:text-brandblue/80" data-testid={`open-office-${o.id}`}>
                      Beheer <ChevronRight size={14} className="ml-1" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showNewOffice && (
        <NewOfficeDialog
          onClose={() => setShowNewOffice(false)}
          onCreated={() => { setShowNewOffice(false); refresh(); }}
        />
      )}
    </div>
  );
}

const SummaryCard = ({ label, value, hint, warn }) => (
  <div className={`rounded-2xl border p-4 shadow-sm ${warn ? "border-amber-300 bg-amber-50" : "border-border bg-white"}`}>
    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className="mt-1 font-display text-2xl font-extrabold text-navy">{value ?? "—"}</div>
    {hint && <div className="mt-0.5 text-xs text-slate-500">{hint}</div>}
  </div>
);

const Th = ({ children }) => <th className="px-5 py-2 text-left font-semibold">{children}</th>;

// ---------------------------------------------------------------------------
// New office dialog
// ---------------------------------------------------------------------------
function NewOfficeDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", kvk: "", website: "", contact_name: "", contact_email: "", contact_phone: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.name.trim()) return toast.error("Naam is verplicht");
    setSaving(true);
    try {
      await api("/admin/partner-offices", { method: "POST", body: JSON.stringify(form) });
      toast.success("Makelaar toegevoegd");
      onCreated();
    } catch (e) { toast.error(e.message || "Toevoegen mislukt"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Nieuwe makelaar toevoegen</DialogTitle></DialogHeader>
        <div className="grid gap-3 py-2">
          <F label="Naam *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="office-name" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="KvK"><Input value={form.kvk} onChange={(e) => setForm({ ...form, kvk: e.target.value })} data-testid="office-kvk" /></F>
            <F label="Website"><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://..." /></F>
          </div>
          <F label="Contactpersoon"><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Contact e-mail"><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></F>
            <F label="Contact telefoon"><Input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} /></F>
          </div>
          <p className="rounded-xl bg-amber-50 p-3 text-xs text-amber-900">De samenwerking start als <strong>Pending</strong>. Je kunt daarna de toestemming vastleggen en feeds koppelen.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={save} disabled={saving} className="bg-brandblue hover:bg-brandblue/90" data-testid="office-save">
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}Toevoegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const F = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold text-slate-600">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Office detail — feeds, consent, items
// ---------------------------------------------------------------------------
function OfficeDetail({ officeId, onBack }) {
  const [office, setOffice] = useState(null);
  const [feeds, setFeeds] = useState([]);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("feeds");
  const [loading, setLoading] = useState(true);
  const [showNewFeed, setShowNewFeed] = useState(false);
  const [showConsent, setShowConsent] = useState(false);
  const [showImpact, setShowImpact] = useState(false);
  const [editingFeed, setEditingFeed] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [o, f, it] = await Promise.all([
        api(`/admin/partner-offices/${officeId}`),
        api(`/admin/partner-offices/${officeId}/feeds`),
        api(`/admin/partner-import-items?office_id=${officeId}&limit=200`),
      ]);
      setOffice(o);
      setFeeds(f.feeds || []);
      setItems(it.items || []);
    } catch (e) { toast.error(e.message || "Kon detail niet laden"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [officeId]);

  const pendingItems = useMemo(() => items.filter((i) => i.status === "pending_review" || i.status === "error"), [items]);

  if (loading || !office) {
    return <div className="flex min-h-[50vh] items-center justify-center text-slate-500"><Loader2 className="mr-2 animate-spin" size={16} /> Laden...</div>;
  }

  const consentOk = office.cooperation_status === "approved" && office.consent_status === "approved";

  return (
    <div className="mx-auto max-w-7xl p-6">
      <nav className="mb-2 text-xs text-slate-500">
        <button onClick={onBack} className="hover:text-brandblue">Makelaar-feeds</button> → {office.name}
      </nav>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-navy">{office.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-slate-600">
            {office.kvk && <span>KvK {office.kvk}</span>}
            {office.website && <a href={office.website} target="_blank" rel="noreferrer" className="text-brandblue underline">{office.website.replace(/^https?:\/\//, "")}</a>}
            <Pill value={office.cooperation_status} />
            <Pill value={office.consent_status} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowConsent(true)} className="rounded-xl" data-testid="btn-edit-consent">
            <Shield size={14} className="mr-2" /> Toestemming
          </Button>
          <Button onClick={() => setShowNewFeed(true)} className="rounded-xl bg-brandblue hover:bg-brandblue/90" data-testid="btn-new-feed">
            <Plus size={14} className="mr-2" /> Feed toevoegen
          </Button>
          <Button variant="outline" onClick={() => setShowImpact(true)} className="rounded-xl border-rose-200 text-rose-600 hover:bg-rose-50" data-testid="btn-delete-office">
            <Trash2 size={14} className="mr-2" /> Verwijderen
          </Button>
        </div>
      </div>

      {(office.consent_status === "revoked" || ["paused", "revoked"].includes(office.cooperation_status)) && (
        <div className="mb-4 flex items-start gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" data-testid="kill-switch-banner">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>
            <strong>Publicatie geblokkeerd.</strong> Toestemming of samenwerking is ingetrokken/gepauzeerd: alle woningen van deze makelaar zijn
            direct gedepubliceerd en synchronisatie is geblokkeerd. Herpublicatie kan alleen handmatig nadat samenwerking én toestemming weer op <em>Approved</em> staan.
          </div>
        </div>
      )}

      {!consentOk && (
        <div className="mb-4 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>
            <strong>Nog geen geldige toestemming / samenwerking.</strong> Je kunt feeds en test-syncs uitvoeren met voorbeeldbestanden,
            maar handmatig goedkeuren is pas toegestaan als zowel samenwerking als toestemming op <em>Approved</em> staan.
          </div>
        </div>
      )}

      <div className="mb-4 border-b border-border">
        <div className="flex gap-6">
          {[
            ["feeds", `Feeds (${feeds.length})`],
            ["review", `Review (${pendingItems.length})`],
            ["all_items", `Alle items (${items.length})`],
          ].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`-mb-px border-b-2 pb-2 text-sm font-semibold ${tab === k ? "border-brandblue text-brandblue" : "border-transparent text-slate-500 hover:text-navy"}`}
              data-testid={`tab-${k}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {tab === "feeds" && (
        <FeedsTab feeds={feeds} officeId={officeId} onEdit={setEditingFeed} onRefresh={refresh} canApprove={consentOk} />
      )}
      {(tab === "review" || tab === "all_items") && (
        <ItemsTab items={tab === "review" ? pendingItems : items} canApprove={consentOk} onRefresh={refresh} officeId={officeId} isReview={tab === "review"} />
      )}

      {showNewFeed && (
        <FeedDialog officeId={officeId} onClose={() => setShowNewFeed(false)} onSaved={() => { setShowNewFeed(false); refresh(); }} />
      )}
      {editingFeed && (
        <FeedDialog officeId={officeId} feed={editingFeed} onClose={() => setEditingFeed(null)} onSaved={() => { setEditingFeed(null); refresh(); }} />
      )}
      {showConsent && (
        <ConsentDialog office={office} onClose={() => setShowConsent(false)} onSaved={() => { setShowConsent(false); refresh(); }} />
      )}
      {showImpact && (
        <ImpactDialog office={office} onClose={() => setShowImpact(false)} onDeleted={onBack} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Feeds tab
// ---------------------------------------------------------------------------
function FeedsTab({ feeds, officeId, onEdit, onRefresh }) {
  const [syncing, setSyncing] = useState(null);
  const sync = async (feed) => {
    setSyncing(feed.id);
    try {
      const r = await api(`/admin/partner-feeds/${feed.id}/sync`, { method: "POST", body: "{}" });
      const run = r.run || {};
      toast.success(`Sync ${run.status}: ${run.items_new || 0} nieuw, ${run.items_updated || 0} gewijzigd, ${run.items_unchanged || 0} ongewijzigd, ${run.items_error || 0} fouten`);
      onRefresh();
    } catch (e) { toast.error(e.message || "Sync mislukt"); }
    finally { setSyncing(null); }
  };
  const togglePause = async (feed) => {
    const next = feed.status === "paused" ? "active" : "paused";
    try {
      await api(`/admin/partner-feeds/${feed.id}`, { method: "PATCH", body: JSON.stringify({ status: next }) });
      onRefresh();
    } catch (e) { toast.error(e.message); }
  };
  const toggleAuto = async (feed) => {
    try {
      await api(`/admin/partner-feeds/${feed.id}`, { method: "PATCH", body: JSON.stringify({ auto_publish_small_price: !feed.auto_publish_small_price }) });
      onRefresh();
    } catch (e) { toast.error(e.message); }
  };
  const del = async (feed) => {
    if (!window.confirm(`Feed "${feed.name}" verwijderen? Bestaande items en gepubliceerde woningen blijven staan.`)) return;
    try { await api(`/admin/partner-feeds/${feed.id}`, { method: "DELETE" }); onRefresh(); }
    catch (e) { toast.error(e.message); }
  };

  if (feeds.length === 0) {
    return <div className="rounded-2xl border border-border bg-white p-10 text-center text-sm text-slate-500">
      Nog geen feeds. Voeg er één toe met een XML/CSV-bron of een website-scraper.
    </div>;
  }
  return (
    <div className="space-y-3" data-testid="feeds-list">
      {feeds.map((f) => (
        <div key={f.id} className="rounded-2xl border border-border bg-white p-5 shadow-sm" data-testid={`feed-${f.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Database size={16} className="text-brandblue" />
                <h3 className="font-display text-lg font-bold text-navy">{f.name}</h3>
                <Pill value={f.status} />
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 uppercase">{f.kind}</span>
              </div>
              <div className="mt-1 text-xs text-slate-500 break-all">
                {f.url || <em>test-mode: ingebouwd voorbeeldbestand</em>}
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-slate-600">
                <span>Auth: <strong>{f.auth_type}</strong>{f.has_credential ? ` · credential #${f.credential_fingerprint}` : ""}</span>
                <span>Interval: elke {f.schedule_hours}u</span>
                <span>Laatste run: {f.last_run_at ? new Date(f.last_run_at).toLocaleString("nl-NL") : "nooit"} {f.last_run_status && <Pill value={f.last_run_status} />}</span>
              </div>
              <label className="mt-3 inline-flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={!!f.auto_publish_small_price} onChange={() => toggleAuto(f)} data-testid={`auto-${f.id}`} />
                Automatisch publiceren bij kleine prijswijzigingen (≤ 5% én ≤ €15.000, alleen voor eerder goedgekeurde woningen)
              </label>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => sync(f)} disabled={syncing === f.id || f.status === "disabled"} data-testid={`sync-${f.id}`}>
                {syncing === f.id ? <Loader2 size={14} className="mr-1 animate-spin" /> : <RefreshCw size={14} className="mr-1" />} Nu synchroniseren
              </Button>
              <Button size="sm" variant="outline" onClick={() => togglePause(f)} data-testid={`pause-${f.id}`}>
                {f.status === "paused" ? <Play size={14} className="mr-1" /> : <Pause size={14} className="mr-1" />}
                {f.status === "paused" ? "Hervatten" : "Pauzeren"}
              </Button>
              <Button size="sm" variant="outline" onClick={() => onEdit(f)}><Settings2 size={14} className="mr-1" /> Instellingen</Button>
              <Button size="sm" variant="outline" onClick={() => del(f)} className="text-rose-600 hover:text-rose-700"><Trash2 size={14} /></Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Items tab (review + alle)
// ---------------------------------------------------------------------------
function ItemsTab({ items, canApprove, onRefresh, officeId, isReview }) {
  const [opened, setOpened] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const approvable = items.filter((i) => i.status === "pending_review" && !(i.errors && i.errors.length));
  const withErrors = items.filter((i) => i.errors && i.errors.length).length;

  const approveAll = async () => {
    if (!window.confirm(`${approvable.length} voorbeeldadvertentie(s) goedkeuren en direct online zetten?`)) return;
    setBulkBusy(true);
    try {
      const r = await api(`/admin/partner-offices/${officeId}/approve-all`, { method: "POST", body: "{}" });
      toast.success(`${r.approved} gepubliceerd${r.skipped_with_errors ? `, ${r.skipped_with_errors} overgeslagen (fouten)` : ""}${r.failed ? `, ${r.failed} mislukt` : ""}`);
      onRefresh();
    } catch (e) { toast.error(e.message || "Bulk goedkeuren mislukt"); }
    finally { setBulkBusy(false); }
  };

  if (items.length === 0) {
    return <div className="rounded-2xl border border-border bg-white p-10 text-center text-sm text-slate-500">Geen items.</div>;
  }
  return (
    <div className="space-y-4">
      {isReview && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-white p-4 shadow-sm">
          <div className="text-sm text-slate-600">
            <strong className="text-navy">{approvable.length}</strong> advertentie(s) klaar om te publiceren
            {withErrors > 0 && <span className="text-rose-600"> · {withErrors} met fouten (worden overgeslagen)</span>}
          </div>
          <Button onClick={approveAll} disabled={bulkBusy || !canApprove || approvable.length === 0}
            className="rounded-xl bg-success hover:bg-success/90" data-testid="btn-approve-all">
            {bulkBusy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Check size={14} className="mr-2" />}
            Alles goedkeuren &amp; publiceren ({approvable.length})
          </Button>
        </div>
      )}
      {!canApprove && isReview && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          Goedkeuren is uitgeschakeld tot zowel <strong>samenwerking</strong> als <strong>toestemming</strong> op <em>approved</em> staan.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((i) => {
          const c = i.canonical || {};
          const photo = (c.photos && c.photos[0]) || null;
          const diffKeys = Object.keys(i.diff_from_previous || {});
          return (
            <div key={i.id} className="overflow-hidden rounded-2xl border border-border bg-white shadow-sm transition hover:shadow-md" data-testid={`item-card-${i.id}`}>
              <div className="relative aspect-[4/3] bg-slate-100">
                {photo
                  ? <img src={photo} alt="" className="h-full w-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  : <div className="flex h-full items-center justify-center text-xs text-slate-400">Geen foto</div>}
                <div className="absolute left-2 top-2"><Pill value={i.status} /></div>
                {c.price ? <div className="absolute bottom-2 right-2 rounded-lg bg-navy/90 px-2.5 py-1 text-sm font-bold text-white">€ {Number(c.price).toLocaleString("nl-NL")}</div> : null}
              </div>
              <div className="p-3">
                <div className="truncate font-semibold text-navy">{c.street} {c.house_number}{c.house_letter ? ` ${c.house_letter}` : ""}</div>
                <div className="truncate text-xs text-slate-500">{c.postcode} {c.city}</div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] text-slate-600">
                  {c.m2 ? <span className="rounded bg-slate-100 px-1.5 py-0.5">{c.m2} m²</span> : null}
                  {c.rooms ? <span className="rounded bg-slate-100 px-1.5 py-0.5">{c.rooms} kamers</span> : null}
                  {c.status ? <span className="rounded bg-slate-100 px-1.5 py-0.5">{c.status}</span> : null}
                  {diffKeys.length ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-amber-800">Δ {diffKeys.join(", ")}</span> : null}
                </div>
                {i.errors && i.errors.length > 0 && <div className="mt-2 text-[11px] text-rose-600">⚠ {i.errors.join(", ")}</div>}
                <button onClick={() => setOpened(i)} className="mt-3 inline-flex items-center text-sm font-semibold text-brandblue hover:text-brandblue/80" data-testid={`view-${i.id}`}>
                  <Eye size={14} className="mr-1" /> Voorbeeldadvertentie bekijken
                </button>
              </div>
            </div>
          );
        })}
      </div>
      {opened && <ItemReviewDialog item={opened} canApprove={canApprove} onClose={() => setOpened(null)} onDone={() => { setOpened(null); onRefresh(); }} />}
    </div>
  );
}

function ItemReviewDialog({ item, canApprove, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState(item);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const c = current.canonical || {};
  const diffEntries = Object.entries(item.diff_from_previous || {});
  const EDIT_FIELDS = [
    ["street", "Straat", "text"], ["house_number", "Huisnr", "number"], ["house_letter", "Toev.", "text"],
    ["postcode", "Postcode", "text"], ["city", "Plaats", "text"],
    ["price", "Prijs (€)", "number"], ["price_condition", "Conditie (kk/von)", "text"],
    ["property_type", "Type", "text"], ["status", "Verkoopstatus", "text"],
    ["m2", "Woonopp. m²", "number"], ["plot_m2", "Perceel m²", "number"],
    ["rooms", "Kamers", "number"], ["bedrooms", "Slaapkamers", "number"],
    ["year_built", "Bouwjaar", "number"], ["energy_label", "Energielabel", "text"],
  ];
  const startEdit = () => {
    const f = {};
    EDIT_FIELDS.forEach(([k]) => { f[k] = c[k] ?? ""; });
    f.description = c.description ?? "";
    setForm(f);
    setEdit(true);
  };
  const saveEdits = async () => {
    setBusy(true);
    try {
      const r = await api(`/admin/partner-import-items/${item.id}`, { method: "PATCH", body: JSON.stringify({ fields: form }) });
      setCurrent(r.item);
      setEdit(false);
      toast.success(r.changed?.length ? `Opgeslagen (${r.changed.length} veld(en))` : "Geen wijzigingen");
    } catch (e) { toast.error(e.message || "Opslaan mislukt"); }
    finally { setBusy(false); }
  };
  const approve = async () => {
    setBusy(true);
    try { await api(`/admin/partner-import-items/${item.id}/approve`, { method: "POST", body: "{}" }); toast.success("Goedgekeurd & gepubliceerd"); onDone(); }
    catch (e) { toast.error(e.message || "Goedkeuren mislukt"); }
    finally { setBusy(false); }
  };
  const reject = async () => {
    const reason = window.prompt("Reden voor afwijzing (optioneel):") || "";
    setBusy(true);
    try { await api(`/admin/partner-import-items/${item.id}/reject`, { method: "POST", body: JSON.stringify({ reason }) }); toast.success("Afgewezen"); onDone(); }
    catch (e) { toast.error(e.message || "Afwijzen mislukt"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{c.street} {c.house_number}{c.house_letter ? ` ${c.house_letter}` : ""}, {c.city}</DialogTitle>
          <DialogDescription>Voorbeeldadvertentie — controleer de gegevens voordat je publiceert.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 text-sm">
          {(() => {
            const photos = Array.isArray(c.photos) ? c.photos : [];
            return (
              <div className="overflow-hidden rounded-2xl border border-border" data-testid="ad-preview">
                <div className="relative aspect-[16/9] bg-slate-100">
                  {photos[0]
                    ? <img src={photos[0]} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    : <div className="flex h-full items-center justify-center text-sm text-slate-400">Geen foto beschikbaar</div>}
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                    <div className="text-lg font-extrabold text-white">{c.street} {c.house_number}{c.house_letter ? ` ${c.house_letter}` : ""}</div>
                    <div className="text-xs text-white/80">{c.postcode} {c.city}</div>
                  </div>
                  {c.price ? <div className="absolute right-3 top-3 rounded-lg bg-success px-3 py-1 text-sm font-bold text-white">€ {Number(c.price).toLocaleString("nl-NL")} {c.price_condition || "k.k."}</div> : null}
                </div>
                {photos.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto bg-slate-50 p-2">
                    {photos.slice(0, 8).map((p, idx) => (
                      <img key={idx} src={p} alt="" className="h-14 w-20 shrink-0 rounded object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-2 p-3 text-xs text-slate-600">
                  {c.m2 ? <span className="rounded-full bg-slate-100 px-2.5 py-1">{c.m2} m² wonen</span> : null}
                  {c.plot_m2 ? <span className="rounded-full bg-slate-100 px-2.5 py-1">{c.plot_m2} m² perceel</span> : null}
                  {c.rooms ? <span className="rounded-full bg-slate-100 px-2.5 py-1">{c.rooms} kamers</span> : null}
                  {c.bedrooms ? <span className="rounded-full bg-slate-100 px-2.5 py-1">{c.bedrooms} slaapkamers</span> : null}
                  {c.year_built ? <span className="rounded-full bg-slate-100 px-2.5 py-1">Bouwjaar {c.year_built}</span> : null}
                  {c.energy_label ? <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-emerald-800">Energie {c.energy_label}</span> : null}
                  {c.property_type ? <span className="rounded-full bg-slate-100 px-2.5 py-1">{c.property_type}</span> : null}
                </div>
              </div>
            );
          })()}
          <div className="flex flex-wrap items-center gap-2">
            <Pill value={current.status} />
            <span className="text-xs text-slate-500">ext: {c.external_id}</span>
            {c.source_url && <a href={c.source_url} target="_blank" rel="noreferrer" className="text-xs text-brandblue underline">bron</a>}
            {current.published_sale_id && <span className="text-xs text-slate-500">published_sale_id: {current.published_sale_id}</span>}
          </div>
          {current.errors && current.errors.length > 0 && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-800">
              <strong>Validatiefouten:</strong> {current.errors.join(", ")}{" "}
              {!edit && <button onClick={startEdit} className="font-semibold underline">Corrigeren</button>}
            </div>
          )}
          {edit ? (
            <div className="rounded-xl border border-brandblue/30 bg-brandblue/5 p-4" data-testid="item-edit-form">
              <div className="mb-3 text-xs font-semibold text-navy">Gegevens corrigeren</div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {EDIT_FIELDS.map(([k, label, type]) => (
                  <div key={k}>
                    <label className="mb-1 block text-[11px] font-medium text-slate-500">{label}</label>
                    <Input type={type} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                      className="h-8 text-xs" data-testid={`edit-${k}`} />
                  </div>
                ))}
              </div>
              <label className="mb-1 mt-3 block text-[11px] font-medium text-slate-500">Omschrijving</label>
              <Textarea rows={4} value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} className="text-xs" data-testid="edit-description" />
              <div className="mt-3 flex gap-2">
                <Button size="sm" onClick={saveEdits} disabled={busy} className="bg-brandblue hover:bg-brandblue/90" data-testid="btn-save-edit">
                  {busy ? <Loader2 size={13} className="mr-1 animate-spin" /> : null} Opslaan
                </Button>
                <Button size="sm" variant="outline" onClick={() => setEdit(false)} disabled={busy}>Annuleren</Button>
              </div>
            </div>
          ) : (
          <div className="grid grid-cols-2 gap-3 rounded-xl bg-slate-50 p-4 text-xs sm:grid-cols-4">
            <Kv label="Prijs" v={c.price && `€ ${Number(c.price).toLocaleString("nl-NL")}`} />
            <Kv label="Oppervlakte" v={c.m2 && `${c.m2} m²`} />
            <Kv label="Perceel" v={c.plot_m2 ? `${c.plot_m2} m²` : "—"} />
            <Kv label="Bouwjaar" v={c.year_built} />
            <Kv label="Energielabel" v={c.energy_label} />
            <Kv label="Kamers" v={c.rooms} />
            <Kv label="Slaapkamers" v={c.bedrooms} />
            <Kv label="Verkoopstatus" v={c.status} />
          </div>
          )}
          {diffEntries.length > 0 && (
            <div className="rounded-xl border border-border p-3 text-xs">
              <div className="mb-2 font-semibold text-slate-700">Wijzigingen t.o.v. vorige run</div>
              <table className="w-full">
                <thead className="text-slate-500"><tr><th className="text-left">Veld</th><th className="text-left">Oud</th><th className="text-left">Nieuw</th></tr></thead>
                <tbody>
                  {diffEntries.map(([k, v]) => (
                    <tr key={k} className="border-t border-border/60">
                      <td className="py-1 font-medium text-slate-700">{k}</td>
                      <td className="py-1 text-slate-500">{formatVal(v.old)}</td>
                      <td className="py-1 text-navy">{formatVal(v.new)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {c.description && (
            <div className="rounded-xl border border-border p-3 text-xs">
              <div className="mb-1 font-semibold text-slate-700">Omschrijving</div>
              <div className="whitespace-pre-wrap text-slate-600">{c.description}</div>
            </div>
          )}
          {!canApprove && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              Goedkeuren is uitgeschakeld tot zowel <strong>samenwerking</strong> als <strong>toestemming</strong> goedgekeurd zijn.
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Sluiten</Button>
          {!edit && <Button variant="outline" onClick={startEdit} disabled={busy} data-testid="btn-edit-item"><Pencil size={14} className="mr-1" /> Bewerken</Button>}
          <Button variant="outline" onClick={reject} disabled={busy} className="text-rose-600" data-testid="btn-reject"><X size={14} className="mr-1" /> Afwijzen</Button>
          <Button onClick={approve} disabled={busy || !canApprove || edit} className="bg-success hover:bg-success/90" data-testid="btn-approve">
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Check size={14} className="mr-1" />} Goedkeuren &amp; publiceren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Kv = ({ label, v }) => (
  <div><div className="text-slate-500">{label}</div><div className="font-semibold text-navy">{v || "—"}</div></div>
);

const formatVal = (v) => {
  if (v === null || v === undefined) return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

// ---------------------------------------------------------------------------
// Feed create/edit dialog
// ---------------------------------------------------------------------------
const SCRAPE_TEMPLATE = {
  list_url: "https://www.voorberg.nl/woningaanbod/koop/",
  detail_link_selector: "a[href*='/woningen/']",
  detail_link_attr: "href",
  max_listings: 25,
  same_host_only: true,
  external_id: { from: "url_slug" },
  address_full: { selector: "h1", attr: "text" },
  fields: {
    price: { selector: "h1 ~ p, .object-price, p.mb-0", attr: "text", regex: "([\\d.]+)" },
    photos: { selector: ".carousel-item-image img", attr: "data-src", multiple: true },
    city: { literal: "Rotterdam" },
  },
  kv: {
    rows_selector: ".object-feature",
    key_selector: ".object-feature-title",
    value_selector: ".object-feature-info",
    map: {
      "Aantal kamers": "rooms",
      "Soort woonhuis": "property_type",
      Status: "status",
      Bouwjaar: "year_built",
      Woonoppervlakte: "m2",
      Perceeloppervlakte: "plot_m2",
      Energielabel: "energy_label",
    },
  },
  page_regex: { postcode: "\\b(\\d{4}\\s?[A-Z]{2})\\b" },
};

function FeedDialog({ officeId, feed, onClose, onSaved }) {
  const editing = !!feed;
  const [form, setForm] = useState(() => ({
    name: feed?.name || "", kind: feed?.kind || "xml", url: feed?.url || "",
    auth_type: feed?.auth_type || "none", auth_header_name: feed?.auth_header_name || "",
    schedule_hours: feed?.schedule_hours || 24, status: feed?.status || "draft",
    auto_publish_small_price: !!feed?.auto_publish_small_price,
    js_render: !!feed?.js_render,
    sample_payload: feed?.sample_payload || "",
    field_mapping_json: feed?.field_mapping ? JSON.stringify(feed.field_mapping, null, 2) : "",
    auth_secret: "", clear_auth_secret: false,
  }));
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const isScrape = form.kind === "scrape";
  const runPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api(`/admin/partner-feeds/${feed.id}/preview-scrape`, { method: "POST", body: "{}" });
      setPreview(r.results || []);
      toast.success(`${r.count} woning(en) gescrapet`);
    } catch (e) { toast.error(e.message || "Preview mislukt"); }
    finally { setPreviewing(false); }
  };
  const save = async () => {
    if (!form.name.trim()) return toast.error("Naam is verplicht");
    let mapping = null;
    if (form.field_mapping_json.trim()) {
      try { mapping = JSON.parse(form.field_mapping_json); }
      catch { return toast.error("Veldmapping is geen geldige JSON"); }
    }
    setSaving(true);
    try {
      const body = {
        name: form.name, kind: form.kind, url: form.url || null,
        auth_type: form.auth_type, auth_header_name: form.auth_header_name || null,
        schedule_hours: Number(form.schedule_hours) || 24, status: form.status,
        auto_publish_small_price: form.auto_publish_small_price,
        js_render: form.js_render,
        sample_payload: form.sample_payload || null,
        field_mapping: mapping,
      };
      if (form.auth_secret) body.auth_secret = form.auth_secret;
      if (form.clear_auth_secret) body.clear_auth_secret = true;
      if (editing) {
        await api(`/admin/partner-feeds/${feed.id}`, { method: "PATCH", body: JSON.stringify(body) });
        toast.success("Feed bijgewerkt");
      } else {
        await api(`/admin/partner-offices/${officeId}/feeds`, { method: "POST", body: JSON.stringify(body) });
        toast.success("Feed toegevoegd");
      }
      onSaved();
    } catch (e) { toast.error(e.message || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };
  const onFile = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setForm((p) => ({ ...p, sample_payload: text }));
    toast.success(`Voorbeeldbestand geladen (${Math.round(text.length / 1024)} KB)`);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Feed bewerken" : "Nieuwe feed"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <F label="Naam *"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="feed-name" /></F>
            <F label="Type">
              <Select value={form.kind} onValueChange={(v) => setForm((f) => ({
                ...f, kind: v,
                field_mapping_json: v === "scrape" && !(f.field_mapping_json || "").includes("detail_link_selector")
                  ? JSON.stringify(SCRAPE_TEMPLATE, null, 2)
                  : f.field_mapping_json,
              }))}>
                <SelectTrigger data-testid="feed-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="xml">XML</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="scrape">Website scraper</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
          {isScrape && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="scrape-note">
              <strong>Website-scraper.</strong> Haalt woningen op van de openbare woningpagina&apos;s via CSS-selectors. Gebruik dit uitsluitend voor makelaars mét getekende publicatietoestemming. Foto&apos;s worden alleen gelinkt (niet gekopieerd). Vul hieronder de overzichts-URL en de scrape-config in.
            </div>
          )}
          {isScrape && (
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-white p-3" data-testid="js-render-toggle">
              <input type="checkbox" checked={form.js_render} onChange={(e) => setForm({ ...form, js_render: e.target.checked })} className="mt-0.5 h-4 w-4" />
              <span className="text-xs text-slate-600">
                <strong className="text-navy">JavaScript-rendering</strong> (voor moderne SPA-sites zoals interhouse/Woonstad). Gebruikt een headless browser — langzamer, maar werkt ook als het woningaanbod via JavaScript laadt. Laat uit voor server-gerenderde sites zoals Voorberg.
              </span>
            </label>
          )}
          <F label={isScrape ? "Overzichts-URL (pagina met het woningaanbod)" : "Feed-URL (http/https, publiek bereikbaar)"}>
            <Input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder={isScrape ? "https://www.voorberg.nl/woningaanbod/koop/" : "https://makelaar.nl/export.xml"} data-testid="feed-url" />
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Authenticatie">
              <Select value={form.auth_type} onValueChange={(v) => setForm({ ...form, auth_type: v })}>
                <SelectTrigger data-testid="feed-auth"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Geen</SelectItem>
                  <SelectItem value="basic">Basic (user:pass)</SelectItem>
                  <SelectItem value="bearer">Bearer token</SelectItem>
                  <SelectItem value="api_key_header">API-key header</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Interval (uren)">
              <Input type="number" min="1" max="168" value={form.schedule_hours} onChange={(e) => setForm({ ...form, schedule_hours: e.target.value })} />
            </F>
          </div>
          {form.auth_type === "api_key_header" && (
            <F label="Header-naam"><Input value={form.auth_header_name} onChange={(e) => setForm({ ...form, auth_header_name: e.target.value })} placeholder="X-API-Key" /></F>
          )}
          {form.auth_type !== "none" && (
            <F label={editing && feed?.has_credential ? `Credential (laat leeg om niet te wijzigen · opgeslagen #${feed.credential_fingerprint})` : "Credential (versleuteld opgeslagen)"}>
              <Input type="password" value={form.auth_secret} onChange={(e) => setForm({ ...form, auth_secret: e.target.value })} placeholder={form.auth_type === "basic" ? "username:password" : "secret"} />
              {editing && feed?.has_credential && (
                <label className="mt-1 flex items-center gap-2 text-xs text-slate-600">
                  <input type="checkbox" checked={form.clear_auth_secret} onChange={(e) => setForm({ ...form, clear_auth_secret: e.target.checked })} /> Opgeslagen credential wissen
                </label>
              )}
            </F>
          )}
          <F label="Status">
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Concept</SelectItem>
                <SelectItem value="active">Actief</SelectItem>
                <SelectItem value="paused">Gepauzeerd</SelectItem>
                <SelectItem value="disabled">Uitgeschakeld</SelectItem>
              </SelectContent>
            </Select>
          </F>
          <label className="flex items-center gap-2 text-xs text-slate-700">
            <input type="checkbox" checked={form.auto_publish_small_price} onChange={(e) => setForm({ ...form, auto_publish_small_price: e.target.checked })} data-testid="feed-auto" />
            Automatisch publiceren bij kleine prijswijzigingen (≤ 5% én ≤ €15.000, alleen voor eerder goedgekeurde woningen)
          </label>

          <F label={isScrape ? "Scrape-config (JSON · CSS-selectors per veld)" : "Veldmapping (JSON · optioneel — laat leeg voor standaardmapping)"}>
            <Textarea rows={isScrape ? 12 : 6} value={form.field_mapping_json} onChange={(e) => setForm({ ...form, field_mapping_json: e.target.value })}
              placeholder={isScrape ? '{"list_url":"...","detail_link_selector":"a[href*=...]", ...}' : '{"external_id":"externId","street":"straat", ...}'} className="font-mono text-xs" data-testid="feed-mapping" />
            {isScrape && (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, field_mapping_json: JSON.stringify(SCRAPE_TEMPLATE, null, 2) })} data-testid="scrape-template-btn">
                  Voorbeeld-config invullen
                </Button>
                {editing && (
                  <Button type="button" size="sm" variant="outline" onClick={runPreview} disabled={previewing} data-testid="scrape-preview-btn">
                    {previewing ? <Loader2 size={13} className="mr-1 animate-spin" /> : <Eye size={13} className="mr-1" />} Test scrapen (preview)
                  </Button>
                )}
                {!editing && <span className="text-[11px] text-slate-500">Sla eerst op, daarna kun je &quot;Test scrapen&quot; draaien.</span>}
              </div>
            )}
          </F>

          {isScrape && preview && (
            <div className="rounded-xl border border-border bg-slate-50 p-3" data-testid="scrape-preview-results">
              <div className="mb-2 text-xs font-semibold text-slate-700">Preview ({preview.length} woning(en)) — niets wordt gepubliceerd</div>
              <div className="space-y-2">
                {preview.map((p, idx) => (
                  <div key={idx} className="rounded-lg bg-white p-2 text-xs">
                    <div className="font-semibold text-navy">{p.canonical.street} {p.canonical.house_number}{p.canonical.house_letter ? ` ${p.canonical.house_letter}` : ""} — {p.canonical.price ? `€ ${Number(p.canonical.price).toLocaleString("nl-NL")}` : "geen prijs"}</div>
                    <div className="text-slate-500">{p.canonical.postcode} {p.canonical.city} · {p.canonical.rooms || "?"} kamers · {p.canonical.photos?.length || 0} foto&apos;s</div>
                    {p.errors?.length > 0 && <div className="mt-1 text-rose-600">⚠ {p.errors.join(", ")}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isScrape && (
          <F label="Test-modus: voorbeeldbestand (XML/CSV)">
            <Textarea rows={5} value={form.sample_payload} onChange={(e) => setForm({ ...form, sample_payload: e.target.value })}
              placeholder="Plak XML/CSV hier óf upload een bestand →" className="font-mono text-xs" />
            <div className="mt-2 flex items-center gap-2">
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-border bg-white px-3 py-1.5 text-xs font-semibold hover:bg-slate-50">
                <Download size={13} /> Bestand uploaden
                <input type="file" accept=".xml,.csv,text/xml,application/xml,text/csv" onChange={onFile} className="hidden" />
              </label>
              {form.sample_payload && <span className="text-xs text-slate-500">{Math.round(form.sample_payload.length / 1024)} KB</span>}
            </div>
            <p className="mt-2 text-[11px] text-slate-500">Een voorbeeldbestand wordt gebruikt door &quot;Nu synchroniseren&quot; — handig om te testen zonder dat de echte feed-URL hoeft te werken.</p>
          </F>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={save} disabled={saving} className="bg-brandblue hover:bg-brandblue/90" data-testid="feed-save">
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}{editing ? "Opslaan" : "Toevoegen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Consent dialog
// ---------------------------------------------------------------------------
function ConsentDialog({ office, onClose, onSaved }) {
  const [form, setForm] = useState({
    cooperation_status: office.cooperation_status || "pending",
    consent_status: office.consent_status || "pending",
    consent_date: office.consent_date || "",
    agreement_filename: office.agreement_filename || "",
    allowed_scope: (office.allowed_scope || []).join(", "),
    allow_photos: office.allow_photos !== false,
    allow_descriptions: office.allow_descriptions !== false,
  });
  const [saving, setSaving] = useState(false);
  const [impact, setImpact] = useState(null);
  useEffect(() => {
    (async () => {
      try { setImpact(await api(`/admin/partner-offices/${office.id}/impact`)); } catch { /* non-blocking */ }
    })();
  }, [office.id]);
  const willBlock = form.consent_status === "revoked" || ["paused", "revoked"].includes(form.cooperation_status);
  const save = async () => {
    setSaving(true);
    try {
      // Update coop status via office PATCH; consent via dedicated endpoint.
      await api(`/admin/partner-offices/${office.id}`, { method: "PATCH", body: JSON.stringify({
        cooperation_status: form.cooperation_status,
      }) });
      await api(`/admin/partner-offices/${office.id}/consent`, { method: "PATCH", body: JSON.stringify({
        consent_status: form.consent_status,
        consent_date: form.consent_date || null,
        agreement_filename: form.agreement_filename || null,
        allowed_scope: form.allowed_scope.split(",").map((s) => s.trim()).filter(Boolean),
        allow_photos: form.allow_photos,
        allow_descriptions: form.allow_descriptions,
      }) });
      toast.success("Opgeslagen");
      onSaved();
    } catch (e) { toast.error(e.message || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>Samenwerking & toestemming</DialogTitle></DialogHeader>
        <div className="grid gap-3 py-2">
          <div className="rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
            Zonder goedgekeurde samenwerking <em>en</em> toestemming kan geen productie-import plaatsvinden en kunnen woningen niet gepubliceerd worden.
            Een ingetrokken toestemming pauzeert automatisch alle feeds van deze makelaar.
          </div>
          {willBlock && (
            <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900" data-testid="consent-block-warning">
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>
                <strong>Let op — directe depublicatie.</strong> Opslaan met deze status depubliceert{" "}
                <strong>{impact?.live_published_listings ?? "…"}</strong> live woning(en) onmiddellijk en blokkeert alle synchronisatie.
                Gegevens blijven bewaard; herpublicatie kan daarna alleen handmatig.
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <F label="Samenwerking">
              <Select value={form.cooperation_status} onValueChange={(v) => setForm({ ...form, cooperation_status: v })}>
                <SelectTrigger data-testid="coop-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">In afwachting</SelectItem>
                  <SelectItem value="approved">Goedgekeurd</SelectItem>
                  <SelectItem value="paused">Gepauzeerd</SelectItem>
                  <SelectItem value="revoked">Ingetrokken</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Toestemming">
              <Select value={form.consent_status} onValueChange={(v) => setForm({ ...form, consent_status: v })}>
                <SelectTrigger data-testid="consent-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">In afwachting</SelectItem>
                  <SelectItem value="approved">Goedgekeurd</SelectItem>
                  <SelectItem value="revoked">Ingetrokken</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Datum toestemming"><Input type="date" value={form.consent_date} onChange={(e) => setForm({ ...form, consent_date: e.target.value })} /></F>
            <F label="Bestandsnaam overeenkomst"><Input value={form.agreement_filename} onChange={(e) => setForm({ ...form, agreement_filename: e.target.value })} placeholder="anker_dpa_2026.pdf" /></F>
          </div>
          <F label="Toegestane scope (komma-gescheiden)">
            <Input value={form.allowed_scope} onChange={(e) => setForm({ ...form, allowed_scope: e.target.value })} placeholder="adresgegevens, prijs, foto&apos;s, omschrijving" />
          </F>
          <div className="flex flex-wrap gap-4 text-xs text-slate-700">
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.allow_photos} onChange={(e) => setForm({ ...form, allow_photos: e.target.checked })} /> Foto&apos;s mogen hergebruikt</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.allow_descriptions} onChange={(e) => setForm({ ...form, allow_descriptions: e.target.checked })} /> Omschrijvingen mogen hergebruikt</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={save} disabled={saving} className="bg-brandblue hover:bg-brandblue/90" data-testid="consent-save">
            {saving ? <Loader2 size={14} className="mr-2 animate-spin" /> : <FileText size={14} className="mr-2" />} Opslaan
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Impact summary (shared) + delete confirmation dialog
// ---------------------------------------------------------------------------
function ImpactStats({ impact }) {
  if (!impact) return null;
  const rows = [
    ["Live gepubliceerde woningen", impact.live_published_listings, true],
    ["Reeds gedepubliceerd", impact.already_depublished],
    ["Totaal gekoppelde woningen", impact.total_linked_listings],
    ["Feeds", impact.feed_count],
    ["In review", impact.pending_items],
    ["Goedgekeurde items", impact.approved_items],
  ];
  return (
    <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-3 text-sm" data-testid="impact-stats">
      {rows.map(([label, v, warn]) => (
        <div key={label} className="flex items-center justify-between gap-2 rounded-lg bg-white px-3 py-2">
          <span className="text-slate-600">{label}</span>
          <span className={`font-bold ${warn && v > 0 ? "text-rose-600" : "text-navy"}`}>{v ?? 0}</span>
        </div>
      ))}
    </div>
  );
}

function ImpactDialog({ office, onClose, onDeleted }) {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    (async () => {
      try { setImpact(await api(`/admin/partner-offices/${office.id}/impact`)); }
      catch (e) { toast.error(e.message || "Kon gevolgen niet laden"); }
      finally { setLoading(false); }
    })();
  }, [office.id]);

  const confirmDelete = async () => {
    setBusy(true);
    try {
      const r = await api(`/admin/partner-offices/${office.id}`, { method: "DELETE" });
      toast.success(`Makelaar verwijderd. ${r.cascaded?.listings_depublished || 0} woning(en) gedepubliceerd.`);
      onDeleted();
    } catch (e) { toast.error(e.message || "Verwijderen mislukt"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="impact-dialog">
        <DialogHeader><DialogTitle>Makelaar verwijderen — gevolgen</DialogTitle></DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center p-8 text-slate-500"><Loader2 className="mr-2 animate-spin" size={16} /> Gevolgen laden...</div>
        ) : (
          <div className="grid gap-3 py-2">
            <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900">
              <AlertTriangle size={18} className="mt-0.5 shrink-0" />
              <div>
                Verwijderen is <strong>onomkeerbaar</strong>. Alle live woningen van <strong>{office.name}</strong> worden
                <strong> direct gedepubliceerd</strong> (verdwijnen uit zoekresultaten, detailpagina&apos;s en sitemap). Feeds, runs en reviewitems worden verwijderd; de woninginhoud blijft ontkoppeld bewaard.
              </div>
            </div>
            <ImpactStats impact={impact} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuleren</Button>
          <Button onClick={confirmDelete} disabled={busy || loading} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-delete-office">
            {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Trash2 size={14} className="mr-2" />} Definitief verwijderen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
